import { useState, useEffect } from 'react'
import { useApp } from '@/store/AppContext'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'
import {
  ShieldAlert,
  Info,
  Grid2X2,
  Settings as SettingsIcon,
} from 'lucide-react'
import { SpaceType } from '@/lib/types'

export default function Settings() {
  const { settings, updateSettings, spaces, updateSpace, generateSpaces } =
    useApp()
  const { profile } = useAuth()
  const { toast } = useToast()

  const [name, setName] = useState(settings.name)
  const [address, setAddress] = useState(settings.address)
  const [timezone, setTimezone] = useState(settings.timezone)
  const [totalSpots, setTotalSpots] = useState(
    settings.totalSpots?.toString() || '50',
  )
  const [error, setError] = useState('')

  useEffect(() => {
    setTotalSpots(settings.totalSpots.toString())
  }, [settings.totalSpots])

  if (profile?.role !== 'admin' && profile?.role !== 'ultramaster') {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-center space-y-4 animate-fade-in-up">
        <ShieldAlert className="h-16 w-16 text-destructive" />
        <h2 className="text-2xl font-bold">Acesso Negado</h2>
        <p className="text-muted-foreground">
          Apenas administradores podem acessar esta página.
        </p>
      </div>
    )
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('O nome do estacionamento é obrigatório.')
      return
    }
    setError('')
    const spotsNum = Number(totalSpots) || 50
    updateSettings({ name, address, timezone, totalSpots: spotsNum })
    if (spotsNum !== spaces.length) {
      generateSpaces(spotsNum)
    }
    toast({
      title: 'Configurações Salvas',
      description: 'As informações foram atualizadas com sucesso.',
    })
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-fade-in-up pb-10">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
        <p className="text-muted-foreground">
          Gerencie as informações gerais e o layout de vagas do estacionamento.
        </p>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" /> Geral
          </TabsTrigger>
          <TabsTrigger value="spaces" className="flex items-center gap-2">
            <Grid2X2 className="w-4 h-4" /> Mapa de Vagas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Card>
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSave} className="space-y-6">
                {error && (
                  <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm font-medium">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3 md:col-span-2">
                    <Label htmlFor="name" className="text-base">
                      Nome do Estacionamento{' '}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="input-mobile-large"
                      placeholder="Ex: Estacionamento Central"
                    />
                  </div>

                  <div className="space-y-3 md:col-span-2">
                    <Label htmlFor="address" className="text-base">
                      Endereço
                    </Label>
                    <Input
                      id="address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="input-mobile-large"
                      placeholder="Ex: Rua das Flores, 123"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="totalSpots" className="text-base">
                      Total de Vagas Físicas
                    </Label>
                    <Input
                      id="totalSpots"
                      type="number"
                      value={totalSpots}
                      onChange={(e) => setTotalSpots(e.target.value)}
                      className="input-mobile-large"
                      placeholder="Ex: 50"
                    />
                    <p className="text-xs text-muted-foreground">
                      Alterar este valor irá recriar o mapa de vagas se for
                      maior.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="timezone" className="text-base">
                      Fuso Horário <span className="text-destructive">*</span>
                    </Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger
                        id="timezone"
                        className="input-mobile-large bg-background"
                      >
                        <SelectValue placeholder="Selecione um fuso horário" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/Sao_Paulo">
                          Brasília (America/Sao_Paulo)
                        </SelectItem>
                        <SelectItem value="America/Manaus">
                          Manaus (America/Manaus)
                        </SelectItem>
                        <SelectItem value="America/Noronha">
                          Fernando de Noronha (America/Noronha)
                        </SelectItem>
                        <SelectItem value="America/New_York">
                          Nova York (America/New_York)
                        </SelectItem>
                        <SelectItem value="UTC">
                          Tempo Universal Coordenado (UTC)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-secondary/10 border-l-4 border-secondary p-4 rounded-md flex items-start gap-3 mt-6">
                  <Info className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground/80">
                    Alterações no fuso horário afetarão como os horários de
                    entrada e saída são exibidos no sistema para cálculos de
                    tempo.
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-mobile-large font-bold shadow-md mt-6"
                >
                  Salvar Configurações
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="spaces" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
                <div>
                  <h3 className="text-lg font-bold">
                    Configuração de Vagas Especiais
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Defina a categoria de cada vaga para visualização no mapa.
                  </p>
                </div>
                <div className="text-sm font-medium bg-muted px-4 py-2 rounded-lg border">
                  Total: {spaces.length} vagas ativas
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
                {spaces.map((space) => (
                  <div
                    key={space.id}
                    className="border rounded-lg p-3 flex flex-col gap-2 items-center bg-card shadow-sm hover:border-primary/50 transition-colors"
                  >
                    <span className="font-bold text-lg bg-muted w-full text-center rounded">
                      {space.number}
                    </span>
                    <Select
                      value={space.type}
                      onValueChange={(v: SpaceType) => {
                        updateSpace(space.id, { type: v })
                        toast({
                          title: 'Vaga Atualizada',
                          description: `Vaga ${space.number} alterada para ${v.toUpperCase()}`,
                        })
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs bg-background border-muted-foreground/30 focus:ring-1 focus:ring-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regular">Normal</SelectItem>
                        <SelectItem value="vip">VIP</SelectItem>
                        <SelectItem value="moto">Moto</SelectItem>
                        <SelectItem value="pcd">PCD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
